/**
 * Constants for the schedule (Gantt) dialog: milestone lifecycle order, phase
 * definitions, and the on-canvas colors for phase bars + the three marker states.
 *
 * Canvas colors are hex strings because SciChart's WebGL renderer takes color
 * literals, not CSS tokens — this is the justified exception to the Carbon-token
 * rule (the marks are drawn on the canvas, never as DOM). DOM chrome (chips, legend
 * swatches, banners) uses Carbon tokens in the `.module.scss`.
 */

import type { MilestoneKey, PhaseKey } from './types';

/** The four editable milestones, in lifecycle order (permit ≤ spud ≤ completion ≤ first prod). */
export const MILESTONE_ORDER: MilestoneKey[] = [
  'permitDate',
  'spudDate',
  'modeledCompletionDate',
  'firstProductionDate',
];

/** Human labels for each milestone (tooltips, picker rows, legend). */
export const MILESTONE_LABELS: Record<MilestoneKey, string> = {
  permitDate: 'Permit',
  spudDate: 'Spud',
  modeledCompletionDate: 'Completion',
  firstProductionDate: 'First production',
};

/**
 * The camelCase milestone key → snake_case calculate-wire field name (spec 2.2).
 * `modeledCompletionDate` maps to `completion_date` (the reported-completion field
 * the BE locked/order check reads).
 */
export const MILESTONE_WIRE_FIELD: Record<MilestoneKey, string> = {
  permitDate: 'permit_date',
  spudDate: 'spud_date',
  modeledCompletionDate: 'completion_date',
  firstProductionDate: 'first_production_date',
};

/**
 * The milestone dates that sync BOTH ways between the Properties/Slot-view grid and the
 * schedule dialog — the SINGLE SOURCE OF TRUTH for that set. The two mirror bridges both
 * derive from this (grid→dialog: `deriveSlotDateOverrides` in view-property-schedule.tsx;
 * dialog→grid: `overlayScheduleDateOverrides` in slot-view.tsx) plus {@link MILESTONE_WIRE_FIELD}
 * for the snake↔camel mapping, so the synced set and its wire mapping live in ONE place.
 *
 * Modeled completion is intentionally excluded: the grid's "Completion Date" column is the
 * read-only REPORTED actual, so a modeled-completion edit has no grid home and stays
 * dialog-only (INS-10671). Each entry is a key of BOTH `OverrideFields` and `SlotRow`, so it
 * can be read off a grid override op and written onto a grid row.
 *
 * Each entry must be a {@link MilestoneKey}; that's enforced where a consumer indexes
 * {@link MILESTONE_WIRE_FIELD} (a `Record<MilestoneKey, …>`) by these values — a typo there
 * is a compile error.
 */
export const GRID_SYNCED_MILESTONES = [
  'permitDate',
  'spudDate',
  'firstProductionDate',
] as const;

export type GridSyncedMilestone = (typeof GRID_SYNCED_MILESTONES)[number];

/**
 * Every override field a committed operation can carry that the SCHEDULE DIALOG must
 * reflect, mapped to the {@link MilestoneKey} row column it lands in — the single source of
 * truth for the dialog's side of the mirror, as {@link GRID_SYNCED_MILESTONES} is for the
 * grid's.
 *
 * ── WHY THIS IS A SUPERSET OF `GRID_SYNCED_MILESTONES` ─────────────────────────────────────
 *
 * That constant answers "which milestones does the GRID both show and originate", and modeled
 * completion is excluded from it for a reason that is still true: the grid's Completion Date
 * column is the read-only reported actual, so the grid cannot originate a completion edit.
 *
 * But the dialog does not only mirror the grid — it re-derives from the whole committed
 * override log, and the TEARSHEET now writes `completionDate` into that log (AC 2 makes
 * `completion_date`, one of the ModelMap::OVERRIDABLE nodes, editable there whenever the well
 * has not reported it). Reading the log through the grid's list drops those operations on the
 * floor: a tearsheet completion edit never reaches the schedule, and the revert of one never
 * clears it. "Which surface can originate this field" and "which fields must the dialog
 * reflect" are two different questions, and answering the second with the first is the bug.
 *
 * KEYED BY THE OverrideFields SPELLING, VALUED BY THE MilestoneKey. For the other three the
 * two coincide (`permitDate` is both); for completion they do NOT — the override field is
 * `completionDate` while the dialog's row column is `modeledCompletionDate`. Indexing
 * `operation.overrides` with the milestone key would look up a key no operation carries and
 * sync nothing, which is the whole reason this is a map and not a longer list.
 *
 * {@link MILESTONE_WIRE_FIELD} composes onto the values to reach the snake_case wire
 * spelling, which is how override-commit.ts derives `PRUNABLE_DATE_FIELDS` from this same map.
 */
export const DIALOG_SYNCED_DATE_FIELDS: Record<string, MilestoneKey> = {
  ...Object.fromEntries(
    GRID_SYNCED_MILESTONES.map((milestone) => [milestone, milestone])
  ),
  completionDate: 'modeledCompletionDate',
};

/** The four phase segments and the [start, end] milestones that bound them. */
export const PHASE_BOUNDS: Record<
  PhaseKey,
  { start: MilestoneKey; end: MilestoneKey | 'open' }
> = {
  permitting: { start: 'permitDate', end: 'spudDate' },
  drilling: { start: 'spudDate', end: 'modeledCompletionDate' },
  completion: { start: 'modeledCompletionDate', end: 'firstProductionDate' },
  // Producing runs from first production to the right edge of the window.
  producing: { start: 'firstProductionDate', end: 'open' },
};

export const PHASE_ORDER: PhaseKey[] = [
  'permitting',
  'drilling',
  'completion',
  'producing',
];

export const PHASE_LABELS: Record<PhaseKey, string> = {
  permitting: 'Permitting',
  drilling: 'Drilling',
  completion: 'Completion',
  producing: 'Producing',
};

/** On-canvas phase-bar fill colors (hex; WebGL literals). v6 mock tokens (INS-10671). */
export const PHASE_COLORS: Record<PhaseKey, string> = {
  permitting: '#8d8d8d', // gray-50
  drilling: '#0f62fe', // blue-60
  completion: '#007d79', // teal-60
  producing: '#42be65', // green-40
};

/** On-canvas marker fill colors for the three provenance states. */
export const MARKER_COLORS = {
  onFile: '#8d8d8d', // gray-50 — reported / locked
  scheduled: '#0f62fe', // blue-60 — scheduled here (standard)
  override: '#ff832b', // orange-40 — set by you (accent)
} as const;

/**
 * Domain floor: dates before this are pre-1900 "garbage" (the mock's Jan-1801
 * artifact). With future-only OFF they are excluded from domain computation and pin
 * to the axis start edge, their true value surfaced in the tooltip.
 */
export const AXIS_MIN_ISO = '1900-01-01';

/** Right-edge tail (months) drawn for the open-ended "Producing" phase. */
export const PRODUCING_TAIL_MONTHS = 24;

/** Sentinel band id for the "Unscheduled" pseudo-group (always ordered last). */
export const UNSCHEDULED_GROUP_ID = null;

export const UNSCHEDULED_GROUP_NAME = 'Unscheduled';
